import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Search,
  Eye,
  Send,
} from "lucide-react";
import api from "../api/client";

const statusConfig = {
  queued: { icon: Clock, color: "text-gray-400", bg: "bg-gray-800", label: "Queued" },
  analyzing: { icon: Search, color: "text-blue-400", bg: "bg-blue-900/30", label: "Analyzing" },
  generating: { icon: Loader2, color: "text-purple-400", bg: "bg-purple-900/30", label: "Generating" },
  reviewing: { icon: Eye, color: "text-amber-400", bg: "bg-amber-900/30", label: "Review" },
  publishing: { icon: Send, color: "text-copper", bg: "bg-copper/10", label: "Publishing" },
  published: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-900/30", label: "Published" },
  failed: { icon: XCircle, color: "text-red-400", bg: "bg-red-900/30", label: "Failed" },
};

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.queued;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon size={12} className={status === "generating" || status === "analyzing" ? "animate-spin" : ""} />
      {config.label}
    </span>
  );
}

export default function ContentQueue({ selectedSite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchItems = () => {
    if (!selectedSite) return;
    setLoading(true);
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api
      .get(`/websites/${selectedSite.id}/content/`, { params })
      .then((res) => setItems(res.data.results || res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, [selectedSite, statusFilter]);

  // Auto-refresh for in-progress items
  useEffect(() => {
    const hasActive = items.some((i) =>
      ["queued", "analyzing", "generating", "publishing"].includes(i.status)
    );
    if (!hasActive) return;
    const interval = setInterval(fetchItems, 10000);
    return () => clearInterval(interval);
  }, [items]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view content
      </div>
    );
  }

  const statuses = ["all", "queued", "analyzing", "generating", "reviewing", "published", "failed"];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Content Pipeline</h2>
        <Link
          to="/content/new"
          className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={14} />
          New Content
        </Link>
      </div>

      {/* Status filters */}
      <div className="flex gap-2 flex-wrap">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              statusFilter === s
                ? "bg-copper/20 text-copper"
                : "bg-dark-300 text-gray-400 hover:text-white"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Content list */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 animate-pulse">
            Loading content...
          </div>
        ) : !items.length ? (
          <div className="p-12 text-center">
            <FileText size={40} className="text-gray-600 mx-auto mb-3" />
            <div className="text-gray-500 mb-4">No content items yet</div>
            <Link
              to="/content/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus size={14} />
              Create First Content
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-4 font-medium">Keyword</th>
                  <th className="text-left py-2.5 px-3 font-medium">Type</th>
                  <th className="text-left py-2.5 px-3 font-medium">Location</th>
                  <th className="text-left py-2.5 px-3 font-medium">Status</th>
                  <th className="text-left py-2.5 px-3 font-medium">Review</th>
                  <th className="text-right py-2.5 px-3 font-medium">Score</th>
                  <th className="text-right py-2.5 px-3 font-medium">Unique</th>
                  <th className="text-left py-2.5 px-3 font-medium">Mode</th>
                  <th className="text-left py-2.5 px-3 font-medium">Created</th>
                  <th className="py-2.5 px-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <Link
                        to={`/content/${item.id}`}
                        className="font-mono text-gray-200 hover:text-copper transition-colors"
                      >
                        {item.target_keyword}
                      </Link>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500">
                      {(item.content_type || "blog_post").replace(/_/g, " ")}
                    </td>
                    <td className="py-3 px-3 text-gray-400">
                      {item.target_location || "—"}
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="py-3 px-3">
                      {item.review_status ? (
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          item.review_status === "reviewed" ? "bg-green-900/20 text-green-400" :
                          item.review_status === "needs_edit" ? "bg-red-900/20 text-red-400" :
                          "bg-amber-900/20 text-amber-400"
                        }`}>
                          {item.review_status === "ai_draft" ? "AI Draft" :
                           item.review_status === "reviewed" ? "Reviewed" : "Needs Edit"}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-3 text-right font-mono">
                      {item.content_score != null ? (
                        <span
                          className={
                            item.content_score >= 80
                              ? "text-green-400"
                              : item.content_score >= 60
                              ? "text-amber-400"
                              : "text-red-400"
                          }
                        >
                          {item.content_score}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-3 text-right font-mono">
                      {item.uniqueness_score != null ? (
                        <span
                          className={
                            item.uniqueness_score >= 70
                              ? "text-green-400"
                              : item.uniqueness_score >= 50
                              ? "text-amber-400"
                              : "text-red-400"
                          }
                        >
                          {item.uniqueness_score}%
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500">
                      {item.publish_mode}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-3">
                      <Link
                        to={`/content/${item.id}`}
                        className="text-copper hover:text-copper-300 text-xs font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
